import React, { useRef, useState, useEffect, useCallback, useMemo, } from 'react';
import { FlashList } from '@shopify/flash-list';
import Animated from 'react-native-reanimated';
import { SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import { getTransactionsFlatlistData, } from 'utils/getTransactions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppDispatch } from 'store/hooks';
import ListLoader from 'design-system/ListLoader';
import ListEmptyView from 'design-system/ListEmptyView';
import TransactionPreviewRow from './TransactionPreviewRow';
import useNavigateTransactionItem from '../hooks/useNavigateTransactionItem';
const keyExtractor = (item) => String(item.id);
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const TransactionsPreview = ({ query, onSelect, isEditingTransactions, isMultipleSelect, selected, showEmpty = true, smallMargin = true, endpoint, initialTransactions, ...flashListProps }) => {
    const [refresh, setRefresh] = useState({});
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const isFetchingAdditional = useRef(false);
    const navigateToTransactionItem = useNavigateTransactionItem();
    const [state, setState] = useState({
        page: 1,
        initialFetch: !initialTransactions,
        pageCount: 0,
        transactions: initialTransactions ?? [],
    });
    const hasMounted = useRef(false);
    useEffect(() => {
        const onFocus = () => {
            if (hasMounted.current) {
                setRefresh({});
            }
            hasMounted.current = true;
        };
        navigation.addListener('focus', onFocus);
        return () => navigation.removeListener('focus', onFocus);
    }, []);
    const getFilterTransactions = useCallback((page) => dispatch(endpoint(query, page)).then((response) => {
        if (!response?.payload?.transactions)
            return;
        if (page === 1) {
            setState((prev) => ({
                ...prev,
                transactions: response.payload.transactions || [],
                initialFetch: false,
                pageCount: response.payload.paging.pageCount || 0,
            }));
        }
        else {
            setState((prev) => ({
                ...prev,
                transactions: prev.transactions.concat(response.payload.transactions),
                page,
            }));
        }
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, query, refresh]);
    useEffect(() => {
        getFilterTransactions(1);
    }, [getFilterTransactions]);
    const onEndReached = useCallback(() => {
        if (state.page < state.pageCount && !isFetchingAdditional.current) {
            isFetchingAdditional.current = true;
            getFilterTransactions(state.page + 1).finally(() => {
                isFetchingAdditional.current = false;
            });
        }
    }, [getFilterTransactions, state.page, state.pageCount]);
    const flashListData = useMemo(() => getTransactionsFlatlistData(state.transactions, false, true, true), [state.transactions]);
    const openItem = useCallback((item) => {
        if (isEditingTransactions && onSelect) {
            onSelect(item);
        }
        else {
            navigateToTransactionItem(item);
        }
    }, [isEditingTransactions, navigateToTransactionItem, onSelect]);
    const renderRow = useCallback(({ item }) => {
        if (!item?.id)
            return null;
        return (<TransactionPreviewRow item={item} onPress={openItem} isEditingTransactions={isEditingTransactions} isMultipleSelect={isMultipleSelect} selected={selected?.id === item.id}/>);
    }, [isEditingTransactions, isMultipleSelect, openItem, selected]);
    const ListEmptyComponent = useMemo(() => {
        if (state.initialFetch) {
            return <ListLoader smallMargin={smallMargin}/>;
        }
        if (showEmpty) {
            return (<>
          <Spacer.H16 />
          <ListEmptyView onCard title="No transactions" subTitle="Try adjusting your filters"/>
        </>);
        }
        return null;
    }, [showEmpty, smallMargin, state.initialFetch]);
    return (<AnimatedFlashList extraData={selected} data={flashListData} renderItem={renderRow} onEndReached={onEndReached} keyExtractor={keyExtractor} onEndReachedThreshold={0.8} ListEmptyComponent={ListEmptyComponent} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16} {...flashListProps} ListFooterComponent={state.initialFetch ? undefined : flashListProps.ListFooterComponent}/>);
};
export default TransactionsPreview;
